import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Trash2, Save, Music } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const PRESET_STRING_COUNTS = [6, 8, 10, 12, 14];

// Lever name patterns - these names indicate knee levers
const LEVER_PATTERNS = ['LKL', 'LKR', 'RKL', 'RKR', 'LKV', 'RKV', 'LK', 'RK'];

// Helper to detect if a control name is a lever based on naming patterns
const isLeverName = (name) => {
  const upperName = (name || '').toUpperCase().trim();
  // Check if it starts with any lever pattern
  return LEVER_PATTERNS.some(pattern => upperName.startsWith(pattern));
};

// Infer control_type from name if not set (migration fallback)
const inferControlType = (ctrl) => {
  if (ctrl.control_type && ctrl.control_type !== '') {
    return ctrl.control_type;
  }
  return isLeverName(ctrl.name) ? 'lever' : 'pedal';
};

export const CopedantEditorPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = id && id !== 'new';

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [stringCount, setStringCount] = useState(10);
  const [strings, setStrings] = useState([]);
  const [controls, setControls] = useState([]);

  useEffect(() => {
    if (isEditing) {
      fetchCopedant();
    } else {
      initializeStrings(10);
    }
  }, [id]);

  const fetchCopedant = async () => {
    try {
      const response = await api.get(`/copedants/${id}`);
      const data = response.data;
      setName(data.name);
      setDescription(data.description || '');
      setStrings(data.strings || []);
      setStringCount(data.strings?.length || 10);
      // Apply migration fallback: infer control_type from name if not set
      setControls((data.controls || []).map(ctrl => ({
        ...ctrl,
        control_type: inferControlType(ctrl)
      })));
    } catch (error) {
      console.error('Error fetching copedant:', error);
      toast.error('Failed to load copedant');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const initializeStrings = (count) => {
    const defaultNotes = ['F#', 'D#', 'G#', 'E', 'B', 'G#', 'F#', 'E', 'D', 'B'];
    const newStrings = Array.from({ length: count }, (_, i) => ({
      string_number: i + 1,
      open_note: defaultNotes[i % defaultNotes.length]
    }));
    setStrings(newStrings);
  };

  const handleStringCountChange = (value) => {
    const count = value === 'custom' ? stringCount : parseInt(value);
    setStringCount(count);
    initializeStrings(count);
    // Reset control changes for new string count
    setControls(controls.map(ctrl => ({
      ...ctrl,
      changes: ctrl.changes.filter(c => c.string_number <= count)
    })));
  };

  const handleCustomStringCount = (value) => {
    const count = Math.max(1, Math.min(20, parseInt(value) || 1));
    setStringCount(count);
    initializeStrings(count);
  };

  const updateStringNote = (stringNumber, note) => {
    setStrings(strings.map(s => 
      s.string_number === stringNumber ? { ...s, open_note: note } : s
    ));
  };

  const addControl = (type = 'pedal') => {
    const existingNames = controls.map(c => c.name);
    let newName;
    
    if (type === 'pedal') {
      // Find next letter for pedal (A, B, C, D, ...)
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      for (let i = 0; i < letters.length; i++) {
        if (!existingNames.includes(letters[i])) {
          newName = letters[i];
          break;
        }
      }
      newName = newName || `P${controls.filter(c => c.control_type === 'pedal').length + 1}`;
    } else {
      // For levers, use LKL, LKR, RKL, RKR pattern
      const leverNames = ['LKL', 'LKR', 'RKL', 'RKR', 'LK1', 'LK2', 'RK1', 'RK2'];
      for (const ln of leverNames) {
        if (!existingNames.includes(ln)) {
          newName = ln;
          break;
        }
      }
      newName = newName || `L${controls.filter(c => c.control_type === 'lever').length + 1}`;
    }
    
    const newControl = {
      name: newName,
      control_type: type,
      changes: []
    };
    setControls([...controls, newControl]);
  };

  const removeControl = (index) => {
    setControls(controls.filter((_, i) => i !== index));
  };

  const updateControlName = (index, newName) => {
    setControls(controls.map((ctrl, i) => {
      if (i !== index) return ctrl;
      // Auto-detect control_type based on the new name (smart default)
      const detectedType = isLeverName(newName) ? 'lever' : 'pedal';
      return { 
        ...ctrl, 
        name: newName,
        // Only auto-update type if it wasn't explicitly set differently
        control_type: detectedType
      };
    }));
  };

  const updateControlType = (index, control_type) => {
    setControls(controls.map((ctrl, i) => 
      i === index ? { ...ctrl, control_type } : ctrl
    ));
  };

  const updateControlChange = (controlIndex, stringNumber, semitoneChange) => {
    setControls(controls.map((ctrl, i) => {
      if (i !== controlIndex) return ctrl;

      const existingChangeIndex = ctrl.changes.findIndex(c => c.string_number === stringNumber);
      let newChanges;

      if (semitoneChange === 0 || semitoneChange === '') {
        newChanges = ctrl.changes.filter(c => c.string_number !== stringNumber);
      } else if (existingChangeIndex >= 0) {
        newChanges = ctrl.changes.map((c, idx) => 
          idx === existingChangeIndex ? { ...c, semitone_change: parseInt(semitoneChange) || 0 } : c
        );
      } else {
        newChanges = [...ctrl.changes, { string_number: stringNumber, semitone_change: parseInt(semitoneChange) || 0 }];
      }

      return { ...ctrl, changes: newChanges };
    }));
  };

  const getControlChange = (controlIndex, stringNumber) => {
    const change = controls[controlIndex]?.changes?.find(c => c.string_number === stringNumber);
    return change?.semitone_change || '';
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    if (strings.length === 0) {
      toast.error('Please add at least one string');
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim() || null,
        strings,
        controls: controls.filter(c => c.name.trim())
      };

      if (isEditing) {
        await api.put(`/copedants/${id}`, data);
        toast.success('Copedant updated!');
      } else {
        await api.post(`/copedants`, data);
        toast.success('Copedant created!');
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving copedant:', error);
      const message = error.response?.data?.detail || 'Failed to save copedant';
      if (message.startsWith('UPGRADE_REQUIRED:')) {
        toast.error(message.replace('UPGRADE_REQUIRED:', ''));
        navigate('/upgrade');
        return;
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ss-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-ss-cyan border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ss-bg">
      <div className="noise-overlay"></div>

      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
              className="text-ss-muted hover:text-ss-text"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight uppercase text-ss-text">
                {isEditing ? 'Edit Copedant' : 'Create Copedant'}
              </h1>
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-copedant-btn"
            className="bg-ss-cyan hover:bg-ss-cyan/90 text-black font-semibold"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Basic Info */}
        <Card className="bg-ss-surface border-ss-border mb-8">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-ss-text">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-ss-muted">Name *</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Custom Copedant"
                  className="mt-1 bg-ss-bg border-ss-border text-ss-text"
                  data-testid="copedant-name-input"
                />
              </div>
              <div>
                <Label className="text-ss-muted">Number of Strings</Label>
                <Select
                  value={PRESET_STRING_COUNTS.includes(stringCount) ? stringCount.toString() : 'custom'}
                  onValueChange={handleStringCountChange}
                >
                  <SelectTrigger className="mt-1 bg-ss-bg border-ss-border text-ss-text" data-testid="string-count-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-ss-surface border-ss-border">
                    {PRESET_STRING_COUNTS.map(count => (
                      <SelectItem key={count} value={count.toString()} className="text-ss-text">
                        {count} strings
                      </SelectItem>
                    ))}
                    <SelectItem value="custom" className="text-ss-text">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {!PRESET_STRING_COUNTS.includes(stringCount) && (
                  <Input
                    type="number"
                    value={stringCount}
                    onChange={(e) => handleCustomStringCount(e.target.value)}
                    min={1}
                    max={20}
                    className="mt-2 bg-ss-bg border-ss-border text-ss-text"
                    data-testid="custom-string-count-input"
                  />
                )}
              </div>
            </div>
            <div>
              <Label className="text-ss-muted">Description (optional)</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A brief description of this copedant"
                className="mt-1 bg-ss-bg border-ss-border text-ss-text"
                data-testid="copedant-description-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Strings Table */}
        <Card className="bg-ss-surface border-ss-border mb-8">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-ss-text flex items-center gap-2">
              <span className="w-1 h-5 bg-ss-cyan rounded-full"></span>
              Strings Configuration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-ss-border">
                    <th className="px-4 py-3 text-left text-xs font-bold text-ss-muted uppercase tracking-wider">String #</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-ss-muted uppercase tracking-wider">Open Note</th>
                  </tr>
                </thead>
                <tbody>
                  {strings.map((string) => (
                    <tr key={string.string_number} className="border-b border-ss-border/50 hover:bg-ss-surface-highlight/50">
                      <td className="px-4 py-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-ss-bg border border-ss-border rounded font-mono text-ss-cyan">
                          {string.string_number}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Select
                          value={string.open_note}
                          onValueChange={(value) => updateStringNote(string.string_number, value)}
                        >
                          <SelectTrigger className="w-24 bg-ss-bg border-ss-border text-ss-text" data-testid={`string-${string.string_number}-note`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-ss-surface border-ss-border">
                            {NOTES.map(note => (
                              <SelectItem key={note} value={note} className="text-ss-text font-mono">
                                {note}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Controls Section */}
        <Card className="bg-ss-surface border-ss-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg text-ss-text flex items-center gap-2">
              <span className="w-1 h-5 bg-ss-red rounded-full"></span>
              Controls (Pedals & Knee Levers)
            </CardTitle>
            <div className="flex gap-2">
              <Button
                onClick={() => addControl('pedal')}
                data-testid="add-pedal-btn"
                size="sm"
                className="bg-ss-cyan hover:bg-ss-cyan/90 text-black"
              >
                <Plus className="w-4 h-4 mr-1" />
                Pedal
              </Button>
              <Button
                onClick={() => addControl('lever')}
                data-testid="add-lever-btn"
                size="sm"
                variant="outline"
                className="border-ss-red text-ss-red hover:bg-ss-red/10"
              >
                <Plus className="w-4 h-4 mr-1" />
                Knee Lever
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {controls.length === 0 ? (
              <div className="text-center py-8 text-ss-muted">
                <Music className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No controls added yet. Add pedals or knee levers above.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-ss-border">
                      <th className="px-3 py-3 text-left text-xs font-bold text-ss-muted uppercase tracking-wider sticky left-0 bg-ss-surface z-10">
                        Control
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-bold text-ss-muted uppercase tracking-wider sticky left-20 bg-ss-surface z-10">
                        Type
                      </th>
                      {strings.map(s => (
                        <th key={s.string_number} className="px-2 py-3 text-center text-xs font-bold text-ss-muted uppercase tracking-wider min-w-[60px]">
                          S{s.string_number}
                        </th>
                      ))}
                      <th className="px-3 py-3 text-center text-xs font-bold text-ss-muted uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {controls.map((control, controlIndex) => (
                      <tr key={controlIndex} className="border-b border-ss-border/50 hover:bg-ss-surface-highlight/50">
                        <td className="px-3 py-3 sticky left-0 bg-ss-surface z-10">
                          <Input
                            value={control.name}
                            onChange={(e) => updateControlName(controlIndex, e.target.value)}
                            placeholder="Name"
                            className="w-16 bg-ss-bg border-ss-border text-ss-text text-center font-mono"
                            data-testid={`control-${controlIndex}-name`}
                          />
                        </td>
                        <td className="px-3 py-3 sticky left-20 bg-ss-surface z-10">
                          <Select
                            value={control.control_type}
                            onValueChange={(value) => updateControlType(controlIndex, value)}
                          >
                            <SelectTrigger className="w-24 bg-ss-bg border-ss-border text-ss-text text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-ss-surface border-ss-border">
                              <SelectItem value="pedal" className="text-ss-text text-xs">Pedal</SelectItem>
                              <SelectItem value="lever" className="text-ss-text text-xs">Knee Lever</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        {strings.map(s => (
                          <td key={s.string_number} className="px-2 py-3">
                            <Input
                              type="number"
                              value={getControlChange(controlIndex, s.string_number)}
                              onChange={(e) => updateControlChange(controlIndex, s.string_number, e.target.value)}
                              placeholder="0"
                              className="w-14 bg-ss-bg border-ss-border text-ss-text text-center font-mono text-sm"
                              data-testid={`control-${controlIndex}-string-${s.string_number}`}
                            />
                          </td>
                        ))}
                        <td className="px-3 py-3 text-center">
                          <Button
                            onClick={() => removeControl(controlIndex)}
                            variant="ghost"
                            size="sm"
                            data-testid={`remove-control-${controlIndex}`}
                            className="text-ss-muted hover:text-ss-red"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-4 text-xs text-ss-muted">
                  Enter semitone changes: positive numbers raise pitch, negative numbers lower pitch.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CopedantEditorPage;
